/**
 * Service for managing Media Button (headset play/pause) detection
 *
 * SINGLETON PATTERN: Initialize once, never tear down.
 * Components just register/unregister callbacks.
 *
 * Uses the MediaSession API with a silent audio stream to capture
 * play/pause button presses from Bluetooth headsets like Voyager Legend.
 */

const STORAGE_KEY = 'media_button_enabled';
const COOLDOWN_MS = 500; // 500ms cooldown between triggers (reduced from 2s)
const VISIBILITY_GRACE_PERIOD_MS = 2000; // Ignore media events for 2s after app becomes visible
const AUDIO_START_GRACE_PERIOD_MS = 500; // Ignore 'play' events for 500ms after silent audio starts
const DUPLICATE_FILTER_MS = 100; // Filter events within 100ms of each other

// Module-level state (persistent singleton)
let isInitialized = false;
let silentAudioElement = null;
let silentAudioUrl = null;
let onTriggerCallback = null;
let keyboardListenerActive = false;

// Timestamps for filtering
let lastGlobalTriggerTime = 0;
let lastVisibleTimestamp = Date.now();
let lastAudioStartTimestamp = 0;
let lastActionTimestamp = 0;

// Flag to ignore events during programmatic audio control
let ignoringEvents = false;

/**
 * Create a silent audio blob for playback
 */
const createSilentAudioBlob = () => {
  const sampleRate = 8000;
  const duration = 1;
  const numSamples = sampleRate * duration;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, numSamples * 2, true);

  return new Blob([buffer], { type: 'audio/wav' });
};

/**
 * Handle Enter key press (for Flic button on Android)
 */
const handleKeyDown = (e) => {
  // Only handle Enter key (Flic keyboard shortcut)
  if (e.key !== 'Enter' && e.code !== 'Enter') {
    return;
  }

  // Don't trigger if user is typing in an input field
  const tagName = e.target.tagName.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea' || e.target.isContentEditable) {
    console.log('[MediaButton] IGNORED: Enter in input field');
    return;
  }

  console.log('[MediaButton] Enter key detected (Flic)');
  handleMediaAction('enter');
};

/**
 * Handle media session action (play or pause)
 */
const handleMediaAction = (action) => {
  // Ignore events during programmatic audio control
  if (ignoringEvents) {
    console.log(`[MediaButton] IGNORED: programmatic control in progress`);
    return;
  }

  const now = Date.now();
  const timeSinceVisible = now - lastVisibleTimestamp;
  const timeSinceAudioStart = now - lastAudioStartTimestamp;
  const timeSinceLastAction = now - lastActionTimestamp;

  console.log(`[MediaButton] Action: ${action} | timeSinceVisible: ${timeSinceVisible}ms | timeSinceAudioStart: ${timeSinceAudioStart}ms | timeSinceLastAction: ${timeSinceLastAction}ms`);

  // Filter duplicate triggers
  if (timeSinceLastAction < DUPLICATE_FILTER_MS) {
    console.log(`[MediaButton] IGNORED: duplicate filter`);
    return;
  }
  lastActionTimestamp = now;

  // Ignore when app is backgrounded
  if (document.visibilityState === 'hidden') {
    console.log('[MediaButton] IGNORED: app is backgrounded');
    return;
  }

  // Ignore shortly after app becomes visible
  if (timeSinceVisible < VISIBILITY_GRACE_PERIOD_MS) {
    console.log(`[MediaButton] IGNORED: visibility grace period`);
    return;
  }

  // Ignore 'play' events shortly after audio started
  if (action === 'play' && timeSinceAudioStart < AUDIO_START_GRACE_PERIOD_MS) {
    console.log(`[MediaButton] IGNORED: audio start grace period`);
    return;
  }

  // Check cooldown
  const timeSinceLastTrigger = now - lastGlobalTriggerTime;
  if (timeSinceLastTrigger < COOLDOWN_MS) {
    console.log('[MediaButton] IGNORED: cooldown active');
    return;
  }

  // Trigger callback if registered
  if (onTriggerCallback) {
    console.log('[MediaButton] TRIGGERING callback!');
    lastGlobalTriggerTime = now;

    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    onTriggerCallback(action);
  } else {
    console.log('[MediaButton] No callback registered');
  }
};

/**
 * Initialize the MediaSession singleton (called once, persists forever)
 */
const initializeMediaSession = () => {
  if (isInitialized) {
    console.log('[MediaButton] Already initialized');
    return true;
  }

  if (!('mediaSession' in navigator)) {
    console.warn('[MediaButton] MediaSession API not supported');
    return false;
  }

  console.log('[MediaButton] Initializing MediaSession singleton...');

  try {
    // Create silent audio
    const silentBlob = createSilentAudioBlob();
    silentAudioUrl = URL.createObjectURL(silentBlob);
    silentAudioElement = new Audio(silentAudioUrl);
    silentAudioElement.loop = true;
    silentAudioElement.volume = 0.01;

    // Audio element events
    silentAudioElement.addEventListener('pause', () => {
      console.log('[MediaButton] Audio element paused');
      handleMediaAction('pause');
    });
    silentAudioElement.addEventListener('play', () => {
      console.log('[MediaButton] Audio element play');
      handleMediaAction('play');
    });

    // MediaSession metadata
    navigator.mediaSession.metadata = new MediaMetadata({
      title: 'Holocron Recording',
      artist: 'Ready to record',
      album: 'Voice Assistant'
    });
    navigator.mediaSession.playbackState = 'playing';

    // MediaSession action handlers (persistent, never cleared)
    navigator.mediaSession.setActionHandler('play', () => {
      console.log('[MediaButton] MediaSession PLAY action (audio was paused:', silentAudioElement?.paused, ')');
      handleMediaAction('play');
      // Resume audio to maintain focus (with event protection)
      if (silentAudioElement?.paused) {
        ignoringEvents = true;
        lastAudioStartTimestamp = Date.now();
        silentAudioElement.play()
          .then(() => setTimeout(() => { ignoringEvents = false; }, 100))
          .catch(() => { ignoringEvents = false; });
      }
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      console.log('[MediaButton] MediaSession PAUSE action (audio was playing:', !silentAudioElement?.paused, ')');
      handleMediaAction('pause');
      // Resume audio IMMEDIATELY to maintain focus (with event protection)
      ignoringEvents = true;
      lastAudioStartTimestamp = Date.now();
      silentAudioElement?.play()
        .then(() => setTimeout(() => { ignoringEvents = false; }, 100))
        .catch(() => { ignoringEvents = false; });
    });

    navigator.mediaSession.setActionHandler('stop', () => {
      console.log('[MediaButton] MediaSession stop action');
      handleMediaAction('pause');
    });

    isInitialized = true;
    console.log('[MediaButton] MediaSession singleton initialized');

    // Try to start audio (may fail due to autoplay policy)
    lastAudioStartTimestamp = Date.now();
    silentAudioElement.play()
      .then(() => console.log('[MediaButton] Silent audio playing'))
      .catch(err => console.warn('[MediaButton] Autoplay blocked:', err.message));

    return true;
  } catch (err) {
    console.error('[MediaButton] Initialization failed:', err);
    return false;
  }
};

// Handle visibility changes
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      lastVisibleTimestamp = Date.now();
      console.log('[MediaButton] App visible, grace period started');
      // Resume audio after delay (with event protection)
      if (isInitialized && silentAudioElement?.paused) {
        setTimeout(() => {
          if (silentAudioElement?.paused) {
            ignoringEvents = true;
            lastAudioStartTimestamp = Date.now();
            silentAudioElement.play()
              .then(() => setTimeout(() => { ignoringEvents = false; }, 100))
              .catch(() => { ignoringEvents = false; });
          }
        }, 500);
      }
    } else {
      console.log('[MediaButton] App hidden, pausing audio');
      ignoringEvents = true;
      silentAudioElement?.pause();
      setTimeout(() => { ignoringEvents = false; }, 100);
    }
  });
}

export const mediaButtonService = {
  /**
   * Check if enabled in localStorage
   */
  isEnabled: () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  },

  enable: () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch {}
  },

  disable: () => {
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch {}
  },

  toggle: () => {
    const current = mediaButtonService.isEnabled();
    if (current) {
      mediaButtonService.disable();
    } else {
      mediaButtonService.enable();
    }
    return !current;
  },

  isSupported: () => 'mediaSession' in navigator,

  /**
   * Register callback for media button presses
   * Initializes MediaSession singleton if needed
   */
  startListening: (onTrigger) => {
    console.log('[MediaButton] startListening called');

    // Initialize singleton if needed
    if (!isInitialized) {
      initializeMediaSession();
    }

    // Register callback
    onTriggerCallback = onTrigger;

    // Add Enter key listener for Flic on Android
    if (!keyboardListenerActive) {
      document.addEventListener('keydown', handleKeyDown);
      keyboardListenerActive = true;
      console.log('[MediaButton] Enter key listener added (Flic support)');
    }

    // Aggressively reclaim media focus by restarting audio
    // This helps when other audio (like recording) may have stolen focus
    const reclaimFocus = () => {
      if (silentAudioElement) {
        ignoringEvents = true; // Don't trigger on our own pause/play
        silentAudioElement.pause();
        lastAudioStartTimestamp = Date.now();
        silentAudioElement.play()
          .then(() => {
            console.log('[MediaButton] Media focus reclaimed');
            setTimeout(() => { ignoringEvents = false; }, 100);
          })
          .catch((err) => {
            console.warn('[MediaButton] Could not reclaim focus:', err.message);
            ignoringEvents = false;
          });
      }
    };

    console.log('[MediaButton] Reclaiming media focus...');
    reclaimFocus();

    // Also retry after a delay in case recording audio is still initializing
    setTimeout(() => {
      console.log('[MediaButton] Delayed focus reclaim...');
      reclaimFocus();
    }, 500);

    console.log('[MediaButton] Callback registered');

    return true;
  },

  /**
   * Unregister callback (does NOT tear down MediaSession)
   */
  stopListening: () => {
    console.log('[MediaButton] stopListening - clearing callback and keyboard listener');
    onTriggerCallback = null;

    // Remove Enter key listener
    if (keyboardListenerActive) {
      document.removeEventListener('keydown', handleKeyDown);
      keyboardListenerActive = false;
    }
  },

  /**
   * Check if MediaSession is initialized
   */
  isListening: () => isInitialized,

  /**
   * Activate audio playback (call after user interaction)
   */
  activate: () => {
    console.log('[MediaButton] activate() called');

    // Initialize if needed
    if (!isInitialized) {
      initializeMediaSession();
    }

    // Try to play audio (with event protection)
    if (silentAudioElement?.paused) {
      ignoringEvents = true;
      lastAudioStartTimestamp = Date.now();
      silentAudioElement.play()
        .then(() => {
          console.log('[MediaButton] Audio activated');
          setTimeout(() => { ignoringEvents = false; }, 100);
        })
        .catch(err => {
          console.warn('[MediaButton] Activation failed:', err.message);
          ignoringEvents = false;
        });
    } else {
      console.log('[MediaButton] Audio already playing');
    }
  },

  // Legacy API compatibility
  canTriggerNow: () => Date.now() - lastGlobalTriggerTime >= COOLDOWN_MS,
  setLastTriggerTime: () => { lastGlobalTriggerTime = Date.now(); }
};
